import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Play, Zap, Brain, Target, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDailyChallengeId } from '../lib/gameStats';

const Hero = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dailyId = useMemo(() => getDailyChallengeId(), []);

  const features = [
    {
      icon: <Brain className="h-7 w-7" />,
      title: t('hero.features.ai.title'),
      description: t('hero.features.ai.description')
    },
    {
      icon: <Target className="h-7 w-7" />,
      title: t('hero.features.games.title'),
      description: t('hero.features.games.description')
    },
    {
      icon: <Zap className="h-7 w-7" />,
      title: t('hero.features.aesthetics.title'),
      description: t('hero.features.aesthetics.description')
    }
  ];

  return (
    <section className="relative min-h-[70vh] flex items-center justify-center pt-20 pb-10">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" style={{ color: 'var(--text-primary)' }}>
            {t('hero.title')}
          </h1>
          <p className="text-lg md:text-xl mb-2 font-medium" style={{ color: 'var(--text-secondary)' }}>
            {t('hero.subtitle')}
          </p>
          <p className="text-base max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            {t('hero.description')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-6"
        >
          <div className="shell-card p-5 max-w-xl mx-auto text-left">
            <div className="flex items-center gap-2 mb-2" style={{ color: 'var(--accent)' }}>
              <Sparkles className="h-5 w-5" />
              <span className="font-bold">{t('daily.title')}</span>
            </div>
            <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>{t('daily.subtitle')}</p>
            <button
              type="button"
              className="shell-button shell-button-primary"
              onClick={() => navigate(`/game/${dailyId}`)}
            >
              {t(`games.${dailyId}`, { defaultValue: dailyId })}
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mb-10"
        >
          <a href="#games" className="shell-button shell-button-primary text-lg px-8 py-3 gap-2">
            <Play className="h-5 w-5" />
            <span>{t('hero.cta')}</span>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
              className="shell-card p-5 text-center"
            >
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-lg mb-3"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
              >
                {feature.icon}
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {feature.title}
              </h3>
              <p style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
